import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star, Trophy, Smartphone, Monitor, Tablet, Sparkles, TrendingUp } from 'lucide-react';
import { PrimeTimesMatrix, ConversionMetric } from '../../hooks/types/primeTimesTypes';
import { Badge } from '@/components/ui/badge';
import {
  calculateChampionConversion,
  calculateTop4WindowsConversion,
  calculateDeviceBestWindowsConversion,
  generateAutoInsightConversion
} from '../utils/peakCalculations';
import { MetricCardHeader } from '../../common/MetricCardHeader';
import { formatBRL } from '../utils/format';

interface PremiumResultPeakCardsProps {
  data: PrimeTimesMatrix;
  metric: ConversionMetric;
}

export function PremiumResultPeakCards({ data, metric }: PremiumResultPeakCardsProps) {
  useLanguage();
  const champion = calculateChampionConversion(data, metric);
  const otherTopWindows = calculateTop4WindowsConversion(data, metric);
  const deviceBestWindows = calculateDeviceBestWindowsConversion(data, metric);
  const autoInsight = generateAutoInsightConversion(data, metric);

  const valueFormatter = (value: number) => {
    if (metric === 'conversions') return `${Math.round(value)} conv`;
    if (metric === 'conversionRate') return `${value.toFixed(1)}%`;
    return formatBRL(value);
  };

  const metricLabel = {
    conversions: 'conversões',
    conversionRate: 'taxa de conversão',
    revenue: 'receita'
  }[metric];

  return (
    <div className="space-y-4">
      {/* Grid: 3 cards lado a lado */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Card 1: Horário Campeão */}
        <Card className="border border-[#E2E8FF] rounded-[12px] bg-white flex flex-col min-h-[280px]">
          <CardContent className="px-[22px] py-[16px] flex-1 flex flex-col gap-[12px]">
            <MetricCardHeader
              icon={Trophy}
              title={translateInterfaceText("Horário campeão do período")}
              subtitle={translateInterfaceText("Melhor período de performance")}
            />

            {/* Período Campeão - Badge azul claro */}
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-normal text-[#6B7280]">{translateInterfaceText("Período campeão")}</span>
              <span className="inline-flex px-3 py-1 rounded-full bg-[#E9F0FF] text-[12px] font-medium text-[#2D6CF6]">
                {champion.weekdayLabel} • {champion.hourLabel}
              </span>
            </div>
            
            <div className="flex flex-col gap-[8px]">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-normal text-[#6B7280]">{translateInterfaceText("Valor")}</span>
                <span className="text-[13px] font-medium text-[#0F172A]">
                  {valueFormatter(champion.value)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-normal text-[#6B7280]">{translateInterfaceText("Conversões")}</span>
                <span className="text-[13px] font-medium text-[#0F172A]">
                  {champion.conversions || 0} {" "}{translateInterfaceText("conv")}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-normal text-[#6B7280]">{translateInterfaceText("Receita")}</span>
                <span className="text-[13px] font-medium text-[#0F172A]">
                  {formatBRL((champion as any).revenue || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-normal text-[#6B7280]">{translateInterfaceText("Dispositivo mais usado")}</span>
                <span className="text-[13px] font-medium text-[#0F172A]">
                  {champion.deviceDominant} ({champion.devicePercentage}%)
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Outros Horários Recomendados */}
        <Card className="border border-[#E2E8FF] rounded-[12px] bg-white flex flex-col min-h-[280px]">
          <CardContent className="px-[22px] py-[16px] flex-1 flex flex-col gap-[12px]">
            <MetricCardHeader
              icon={TrendingUp}
              title={translateInterfaceText("Baseado no desempenho do período")}
              subtitle={translateInterfaceText("Horários alternativos recomendados")}
            />

            <div className="flex flex-col gap-[8px]">
              {otherTopWindows.slice(0, 3).map((window, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between px-4 py-3 rounded-[10px] min-h-[46px] transition-colors ${
                    i === 0
                      ? 'bg-[#EBF2FF] border border-[#3273FF]'
                      : 'bg-[#F7F9FF] border border-[#E2E8FF]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-normal text-[#6B7280]">{i + 1}º</span>
                    <span className="text-[13px] font-normal text-[#0F172A]">
                      {window.weekdayLabel} • {window.hourLabel}
                    </span>
                  </div>
                  <span className="text-[13px] font-medium text-[#0F172A]">
                    {valueFormatter(window.value)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Melhor Horário por Dispositivo */}
        <Card className="border border-[#E2E8FF] rounded-[12px] bg-white flex flex-col min-h-[280px]">
          <CardContent className="px-[22px] py-[16px] flex-1 flex flex-col gap-[12px]">
            <MetricCardHeader
              icon={Smartphone}
              title={translateInterfaceText("Horários ideais por tipo de acesso")}
              subtitle={translateInterfaceText("Comparação dos melhores horários por device")}
            />

            <div className="flex flex-col gap-[8px]">
              <div className="flex items-center justify-between px-4 py-3 bg-[#F7F9FF] rounded-[10px] border border-[#E2E8FF] min-h-[46px]">
                <div className="flex items-center gap-2">
                  <Monitor className="h-4 w-4 text-[#6B7280]" />
                  <span className="text-[13px] font-medium text-[#0F172A]">{translateInterfaceText("Desktop")}</span>
                </div>
                <span className="inline-flex px-3 py-1 rounded-full border border-[#93C5FD] text-[12px] font-medium text-[#2D6CF6]">
                  {deviceBestWindows.desktop}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-[#F7F9FF] rounded-[10px] border border-[#E2E8FF] min-h-[46px]">
                <div className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-[#6B7280]" />
                  <span className="text-[13px] font-medium text-[#0F172A]">{translateInterfaceText("Mobile")}</span>
                </div>
                <span className="inline-flex px-3 py-1 rounded-full border border-[#93C5FD] text-[12px] font-medium text-[#2D6CF6]">
                  {deviceBestWindows.mobile}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-[#F7F9FF] rounded-[10px] border border-[#E2E8FF] min-h-[46px]">
                <div className="flex items-center gap-2">
                  <Tablet className="h-4 w-4 text-[#6B7280]" />
                  <span className="text-[13px] font-medium text-[#0F172A]">{translateInterfaceText("Tablet")}</span>
                </div>
                <span className="inline-flex px-3 py-1 rounded-full border border-[#93C5FD] text-[12px] font-medium text-[#2D6CF6]">
                  {deviceBestWindows.tablet}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Insight Automático */}
      <div className="bg-[#F3F7FF] rounded-[14px] px-4 py-3 border border-[#D4E1FF]">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-5 w-5 text-[#2D66F5]" />
          </div>
          <div className="flex-1">
            <h4 className="text-[14px] font-medium text-[#0F172A] mb-1.5">
              {translateInterfaceText("💡 Insight Automático")}</h4>
            <p className="text-[13px] font-normal text-[#344054] leading-normal">
              {autoInsight.text}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
